import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Layout, Database, Shield, Cpu, Zap, CheckCircle, Code, Server, Globe } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

const REQUIREMENTS = [
    { id: 'ui', title: 'Interface', icon: Layout, desc: 'Build the layout using Grid & Flexbox', module: 'ui-ux' },
    { id: 'db', title: 'Memory', icon: Database, desc: 'Connect a database for Tasks', module: 'production' },
    { id: 'ai', title: 'Intelligence', icon: Cpu, desc: 'Integrate the AI Assistant', module: 'ai-operator' },
    { id: 'sec', title: 'Security', icon: Shield, desc: 'Harden against XSS & Injection', module: 'cyber-security' },
    { id: 'api', title: 'Connectivity', icon: Globe, desc: 'Fetch Weather & Stock Data', module: 'foundation' },
];

export const GorillaOS = () => {
    const { currentUser, progress } = useAcademyStore();
    // In a real implementation, we'd check specific module completion
    // For now, we simulate progress

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-white flex flex-col">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-green-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-2 text-yellow-500 font-bold bg-yellow-500/10 px-4 py-1 rounded-full border border-yellow-500/20">
                    <Zap size={18} fill="currentColor" /> CAPSTONE PROJECT
                </div>
            </div>

            <main className="flex-1 max-w-7xl mx-auto w-full p-8">
                <header className="text-center mb-16">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="inline-block"
                    >
                        <h1 className="text-6xl font-black mb-4 font-comic tracking-tight bg-gradient-to-r from-green-400 via-blue-500 to-purple-500 text-transparent bg-clip-text drop-shadow-sm">
                            Gorilla OS 1.0
                        </h1>
                    </motion.div>
                    <p className="text-2xl text-slate-400 max-w-2xl mx-auto">
                        The ultimate test of your skills. Build your own Web Operating System.
                    </p>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Left: Component Checklist */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-bold text-slate-300 border-b border-slate-700 pb-2">System Components</h2>
                        {REQUIREMENTS.map((req, i) => (
                            <motion.div
                                key={req.id}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: i * 0.1 }}
                                className="bg-slate-800 p-4 rounded-xl border border-slate-700 flex items-center gap-4 group hover:border-green-500/50 transition-colors"
                            >
                                <div className="bg-slate-900 p-3 rounded-lg text-slate-400 group-hover:text-green-400 transition-colors">
                                    <req.icon size={24} />
                                </div>
                                <div>
                                    <div className="font-bold text-lg">{req.title}</div>
                                    <div className="text-sm text-slate-500">{req.desc}</div>
                                </div>
                                <div className="ml-auto w-6 h-6 rounded-full border-2 border-slate-600 group-hover:border-green-500" />
                            </motion.div>
                        ))}
                    </div>

                    {/* Center: The Workspace (Preview) */}
                    <div className="lg:col-span-2 relative">
                        <div className="absolute inset-0 bg-green-500/5 blur-3xl -z-10" />

                        <div className="bg-slate-950 rounded-2xl border-4 border-slate-800 shadow-2xl overflow-hidden min-h-[500px] flex flex-col">
                            {/* Fake OS Toolbar */}
                            <div className="bg-slate-900 p-2 flex items-center gap-2 border-b border-slate-800">
                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                <div className="w-3 h-3 rounded-full bg-green-500" />
                                <div className="bg-slate-800 px-4 py-1 rounded text-xs text-slate-400 font-mono ml-4 flex-1 text-center">
                                    gorilla-os-preview
                                </div>
                            </div>

                            {/* OS Desktop */}
                            <div className="flex-1 p-8 grid grid-cols-2 md:grid-cols-4 gap-4 relative">
                                {/* Desktop Icons */}
                                <div className="text-center group cursor-pointer opacity-50 hover:opacity-100">
                                    <div className="w-16 h-16 bg-blue-500 rounded-2xl mb-2 mx-auto flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                                        <Database className="text-white" size={32} />
                                    </div>
                                    <span className="text-xs font-bold shadow-black drop-shadow-md">My Data</span>
                                </div>
                                <div className="text-center group cursor-pointer opacity-50 hover:opacity-100">
                                    <div className="w-16 h-16 bg-purple-500 rounded-2xl mb-2 mx-auto flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                                        <Cpu className="text-white" size={32} />
                                    </div>
                                    <span className="text-xs font-bold shadow-black drop-shadow-md">AI Chat</span>
                                </div>

                                {/* Placeholder Center Widget */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                                    <div className="text-6xl mb-4 animate-pulse">🦍</div>
                                    <h3 className="text-2xl font-bold text-slate-600">OS Not Installed</h3>
                                    <p className="text-slate-700">Complete modules to acquire parts.</p>
                                </div>
                            </div>

                            {/* Fake Taskbar */}
                            <div className="bg-slate-900/80 backdrop-blur p-3 flex justify-center gap-4">
                                <div className="w-10 h-10 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" />
                                <div className="w-10 h-10 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" />
                                <div className="w-10 h-10 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" />
                            </div>
                        </div>

                        <div className="text-center mt-8">
                            <button className="px-8 py-4 bg-slate-800 text-slate-500 font-bold rounded-2xl border border-slate-700 cursor-not-allowed">
                                Install OS (Requires All Badges)
                            </button>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
};
